import { useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useMutation, useQuery } from "react-query";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import { backendApis } from "../../config";
import { useAuth } from "../../hooks/useAuth";
import eventBus from "../../utils/EventBus";
import {
  formDataForFormBuider,
  formatDate,
  formatNumberSystem,
  waitFor,
} from "../../utils/helpers";
import { cacheTimes, queryActions } from "../../utils/reactQueryActions";
import ConfirmDialog from "../ConfirmDialogue";
import { Logo } from "../Icons";
import ModalLoader from "../utils/ModalLoader";
import { useStoreState } from "easy-peasy";
import { isEmpty } from "lodash";
import CloudflareRobotCheckModal from "../CloudflareRobotCheckModal";
import { useLatestRef, useSessionStorage } from "../../utils/hooks";
import { validateForm } from "../../utils/validator";
import ValidationSummarySection from "./ValidationSummarySection";
import JobPicker from "../Jobs/JobPicker";
import { v4 as uuidv4 } from "uuid";

export default function FillForm({ isPublic }) {
  // FOR DEDUPLICATION OF DRAFTS: use a clientKey stored in sessionStorage to scope which draft belongs to which tab/window.
  const storageKey = `clientKey:${window.location.pathname}`;
  const [clientKey] = useSessionStorage(storageKey, uuidv4());

  const { id, company } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Capture the draftId that was in the URL when the component first mounted.
  // This distinguishes "user resuming a draft" (draftId on load) from
  // "autosave just wrote draftId into the URL" (should not trigger a query).
  const initialDraftIdRef = useRef(
    new URLSearchParams(window.location.search).get("draftId")
  );
  const draftId = useMemo(
    () => new URLSearchParams(location.search).get("draftId"),
    [location.search]
  );

  //  get company from  url,  because in  public  form  the user  is  not logged  in
  const { url: backendUrl } = backendApis.find(el => el.name === company);
  const { user: authUser } = useAuth();
  const currentLoggedInCompany = useStoreState(
    state => state.currentLoggedInCompany
  );
  const iframeRef = useRef();
  const [isLoadingIframe, setIsLoadingIframe] = useState(true);
  const formik = useFormik({
    initialValues: {
      FormID: id,
      UserName: authUser?.Name || "",
      Staff_ID: authUser?.Staff_ID || "",
      filledFormData: "",
      formName: "",
      isMultiStep: false,
      jobNumber: "",
      jobsTiedToForm: [],
    },
    validationSchema: yup.object().shape({
      formName: yup.string().required(""),
      UserName: yup.string().required("Please Enter your name"),
    }),
    onSubmit: async values => {
      if (isEmpty(values.UserName)) {
        formik.setFieldError("UserName", "Please Enter your name");
        toast.error("Please Enter your name");
        return;
      }

      if (
        !isEmpty(formik.values?.jobsTiedToForm) &&
        isEmpty(formik.values?.jobNumber)
      ) {
        formik.setFieldError("jobNumber", "Please select a job");
        toast.error("Please select a job");
        return;
      }

      const onSuccess = data => {
        // Destroy draft state — clear ref and URL param
        autosavedIdRef.current = null;
        initialDraftIdRef.current = null;
        // Use window.location to get the freshest URL (avoids stale closure)
        const params = new URLSearchParams(window.location.search);
        params.delete("draftId");
        const newSearch = params.toString();
        navigate(
          `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}`,
          { replace: true }
        );
        // Reset autosave mutation states so "Saved ✅" disappears
        fillAutoSaveFormMutation.reset();
        editFilledAutoSaveFormMutation.reset();

        // Suppress autosave while the iframe resets to the blank template.
        // The iframe fires AUTOSAVE as it processes the new template which
        // would otherwise create a brand-new draft and put draftId back in the URL.
        // Clear after a short window — enough for the initial burst to pass.
        suppressAutosaveRef.current = true;
        setTimeout(() => {
          suppressAutosaveRef.current = false;
        }, 3000);

        // Clear form
        sendSetupData();

        ConfirmDialog({
          type: "success",
          title: "Form Submitted",
          choice: false,
        });
      };

      const onError = ({ errors }) => {
        if (errors) formik.setErrors(errors);
      };

      if (autosavedIdRef.current) {
        // Draft already exists — update it to a real submission
        editFilledAutoSaveFormMutation.mutate(
          { ...values, id: autosavedIdRef.current, isSubmit: true },
          { onSuccess, onError }
        );
      } else {
        fillFormMutation.mutate(
          { ...values, isSubmit: true },
          { onSuccess, onError }
        );
      }
    },
  });

  const fillForm = async payload => {
    payload.Template = payload.filledFormData;
    const formData = formDataForFormBuider(payload);

    let response = await fetch(`${backendUrl}/api/forms/fill-form`, {
      method: "POST",
      credentials: "include",
      body: formData,
      headers: {
        "x-client-key": clientKey,
      },
    });

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const res = await response.json();
    return res;
  };

  const fillFormMutation = useMutation(payload => fillForm(payload), {
    onSuccess: ({ message, data }) => {
      toast.success(message);
    },
    onError: () => {
      toast.error(`Unable to perform action`);
    },
  });

  // Use a ref to always have fresh formik values inside the message handler
  const formikValuesRef = useRef(formik.values);

  useEffect(() => {
    formikValuesRef.current = formik.values;
  }, [formik.values]);

  // Ref to track if an autosave draft has already been created (holds the draft's ID).
  // Initialized from URL ?draftId= so resuming a copied link picks up where left off.
  const autosavedIdRef = useRef(draftId || null);
  // When true, autosave messages from the iframe are ignored (e.g. during post-submit reset).
  const suppressAutosaveRef = useRef(false);

  const setformData = async data => {
    formik.setFieldValue("filledFormData", data);

    const validationResult = validateForm(data);

    if (!validationResult?.isValid) {
      ConfirmDialog({
        title: "Missing Required Fields",
        description: (
          <ValidationSummarySection
            report={validationResult}
            isOpenByDefault={true}
          />
        ),
        type: "danger",
        choice: false,
      });
      return;
    }

    if (
      await ConfirmDialog({
        title: "Submit",
        description: "Are you sure, you want to submit",
      })
    ) {
      formik.submitForm();
    }
  };

  //----------------------------------------------------------------------
  // fetch data for form
  const getDepartments = async () => {
    // await waitFor(5000);
    let response = await fetch(
      `${backendUrl}/api/users/departments-with-users`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();

    return data;
  };

  const {
    data = { departments: [], staffs: [], jobGrades: [] },
    isFetching,
  } = useQuery(
    [queryActions.GET_USERS_IN_DEPARTMENTS],
    () => getDepartments(),
    {
      enabled: true,
      keepPreviousData: true,
      cacheTime: cacheTimes.GET_USERS_WITH_DEPARTMENTS,
    }
  );

  //------------------------
  //  GEt Form  for filling
  const getForm = async () => {
    let response = await fetch(`${backendUrl}/api/forms/get-form/${id}`, {
      method: "GET",
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();
    if (data?.form.Title) {
      formik.setFieldValue("formName", data?.form.Title);
    }
    if (data?.form) {
      formik.setFieldValue("isMultiStep", Boolean(data?.form.isMultiStep));
    }

    if (data.jobsTiedToForm) {
      formik.setFieldValue("jobsTiedToForm", data?.jobsTiedToForm);

      // if only one job is tied to form, then set that job as default value
      if (data?.jobsTiedToForm?.length === 1) {
        formik.setFieldValue("jobNumber", data?.jobsTiedToForm[0]?.jobNumber);
      }
    }

    if (!data?.form?.enabled) {
      setIsLoadingIframe(false);
    }
    return data;
  };

  const getFormQuery = useQuery(["GET_FORM"], () => getForm(), {
    enabled: true,
  });

  //------------------------
  // Get draft form for resuming (only when ?draftId= is in the URL)
  const getDraftForm = async () => {
    let response = await fetch(
      `${backendUrl}/api/forms/get-filled-form/${draftId}?withParentSettings=true`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();
    if (data?.form.formName) {
      formik.setFieldValue("formName", data?.form.formName);
    }
    if (data?.form.UserName) {
      formik.setFieldValue("UserName", data?.form.UserName);
    }
    if (data?.form.jobNumber) {
      formik.setFieldValue("jobNumber", data?.form.jobNumber);
    }
    return data;
  };

  const getDraftFormQuery = useQuery(
    ["GET_DRAFT_FORM", draftId],
    () => getDraftForm(),
    // Only run when the draftId was already in the URL on initial load (resume scenario).
    // When autosave adds draftId mid-session we don't want to re-fetch.
    {
      enabled:
        !!initialDraftIdRef.current && draftId === initialDraftIdRef.current,
    }
  );

  const setOrientation = async printOrientation => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          type: "OREINTATION",
          data: {
            ...printOrientation,
          },
        },
        process.env.REACT_APP_FORM_BUILDER_URL
      );
    }
  };

  // Add refs to track latest values
  const dataRef = useLatestRef(data);
  const getFormQueryRef = useLatestRef(getFormQuery);
  const getDraftFormQueryRef = useLatestRef(getDraftFormQuery);

  const sendSetupData = async () => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentWindow) {
      const currentData = dataRef.current;
      const currentFormQuery = getFormQueryRef.current;
      const currentDraftFormQuery = getDraftFormQueryRef.current;

      // When resuming from a draft, send the previously filled data as the template
      const template =
        autosavedIdRef.current &&
        currentDraftFormQuery.data?.form?.filledFormData
          ? JSON.parse(currentDraftFormQuery.data.form.filledFormData)
          : JSON.parse(currentFormQuery.data.form.Template);

      iframe.contentWindow.postMessage(
        {
          data: {
            ...data,
            template,
            backendUrl,
            companyData: currentLoggedInCompany,
            formInfo: {
              formNumber: formatNumberSystem(
                currentFormQuery.data.form.formNumberPrefix,
                currentFormQuery.data.form.currentNumber
              ),
              documentNumber: currentFormQuery.data.form?.documentNumber,
              revision: currentFormQuery.data.form.revision,
              date: formatDate(currentFormQuery.data.form.Date_Log, "MMM yyyy"),
              Title: currentFormQuery.data.form?.Title,
            },
          },
          settings: { canFill: true, showTableCheckbox: false },
        },
        process.env.REACT_APP_FORM_BUILDER_URL
      );
    }
  };

  // AUTOSAVE — edit an already-created draft
  const editFilledAutosaveDraft = async payload => {
    payload.Template = payload.filledFormData;
    const formData = formDataForFormBuider(payload);

    let response = await fetch(`${backendUrl}/api/forms/edit-filled-form`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const res = await response.json();
    return res;
  };

  const fillAutoSaveFormMutation = useMutation(payload => fillForm(payload), {
    onSuccess: ({ data }) => {
      if (data?.id) {
        autosavedIdRef.current = data.id;
        // Update URL so user can copy the link and resume later
        const params = new URLSearchParams(location.search);
        params.set("draftId", data.id);
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    },
    onError: () => {
      toast.error(`Unable to perform action`);
    },
  });

  const editFilledAutoSaveFormMutation = useMutation(
    payload => editFilledAutosaveDraft(payload),
    {
      onSuccess: () => {
        console.log("Done");
      },
      onError: () => {
        toast.error(`Unable to perform action`);
      },
    }
  );

  const setAutosaveFormData = data => {
    // Skip autosave while the form is being reset after a submit.
    if (suppressAutosaveRef.current) return;
    formik.setFieldValue("filledFormData", data);
    if (autosavedIdRef.current) {
      editFilledAutoSaveFormMutation.mutate({
        ...formikValuesRef.current,
        id: autosavedIdRef.current,
        filledFormData: data,
        autosave: true,
      });
    } else {
      fillAutoSaveFormMutation.mutate({
        ...formikValuesRef.current,
        filledFormData: data,
        autosave: true,
      });
    }
  };

  const handleMessage = message => {
    if (message.data.type === "CLOSE") {
      //
    } else if (message.data.type === "SAVE") {
      setformData(message.data.value);
    } else if (message.data.type === "AUTOSAVE") {
      if (!isEmpty(message.data.value)) {
        setAutosaveFormData(message.data.value);
      }
    } else if (message.data.type === "FORM_BUILDER_MOUNTED") {
      if (getFormQueryRef.current.data?.form) {
        sendSetupData();
        setOrientation({
          orientation: getFormQueryRef.current.data?.form.orientation,
          pageSize: getFormQueryRef.current.data?.form.pageSize,
        });
      }
    }
  };

  useEffect(() => {
    window.addEventListener("message", handleMessage);

    // cleanup this component
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const save = () => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          type: "SUBMIT",
        },
        process.env.REACT_APP_FORM_BUILDER_URL
      );
    }
  };

  eventBus.useCustomEventListener(
    "SEND_FILE_TO_IFRAME",
    ({ files, elementId, elementType }) => {
      // console.log(files, elementId, elementType);
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            data: {
              files,
              elementId,
              elementType,
            },
            type: "FILES_FROM_PARENT",
          },
          process.env.REACT_APP_FORM_BUILDER_URL
        );
      }
    }
  );

  const [showRobotCheckModal, setShowRobotCheckModal] = useState(
    Boolean(process.env?.REACT_APP_CLOUDFLARE_SITE_KEY) && isPublic
  );

  const isLoadingData =
    isFetching ||
    getFormQuery.isFetching ||
    (!!initialDraftIdRef.current && getDraftFormQuery.isFetching);

  return (
    <>
      <div>
        {!formik.values?.isMultiStep && !isLoadingData ? (
          <Form.Group>
            <div className="py-3 px-3">
              <div className="row d-flex justify-content-center gap-3 flex-md-nowrap">
                <div className="col-md-3">
                  <Form.Control
                    name="formName"
                    placeholder="Form Name"
                    required
                    value={formik.values.formName}
                    onChange={formik.handleChange}
                    isInvalid={
                      formik.touched.formName && !!formik.errors.formName
                    }
                  />{" "}
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.formName}
                  </Form.Control.Feedback>
                </div>

                <div className="col-md-3 d-flex align-items-center gap-2">
                  <Form.Label className="fw-6 text-nowrap m-0">
                    Name
                    <span className="text-danger">*</span>:
                  </Form.Label>
                  <div className="flex-grow-1">
                    <Form.Control
                      name="UserName"
                      placeholder="Enter Your name"
                      value={formik.values.UserName}
                      onChange={formik.handleChange}
                      isInvalid={
                        formik.touched.UserName && !!formik.errors.UserName
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.UserName}
                    </Form.Control.Feedback>
                  </div>
                </div>

                {!isEmpty(formik?.values?.jobsTiedToForm) && (
                  <div className="col-md-3 d-flex align-items-center gap-2">
                    <Form.Label className="fw-6 text-nowrap m-0">
                      Job / Contract
                      <span className="text-danger">*</span>:
                    </Form.Label>
                    <div className="flex-grow-1">
                      <JobPicker
                        value={formik.values.jobNumber}
                        onChange={value =>
                          formik.setFieldValue("jobNumber", value)
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Form.Group>
        ) : null}

        {!isLoadingData ? (
          getFormQuery?.data?.form?.enabled ? (
            <>
              <iframe
                ref={iframeRef}
                id="Frame"
                onLoad={e => {
                  setIsLoadingIframe(false);
                }}
                className="w-100 border"
                style={{
                  backgroundColor: "#E5E7EB",
                  minHeight: "calc(100vh - 10rem)",
                  ...(formik.values?.isMultiStep
                    ? { minHeight: "calc(100vh)" }
                    : {}),
                }}
                title="Fill Form"
                src={
                  formik.values?.isMultiStep
                    ? `${
                        process.env.REACT_APP_FORM_BUILDER_URL
                      }/form/fill-steps`
                    : `${process.env.REACT_APP_FORM_BUILDER_URL}/form/fill`
                }
              />
              <Form noValidate onSubmit={formik.handleSubmit}>
                <input type={"hidden"} value={formik.values.FormID} />
              </Form>
              <div
                className={`action-area w-100 p-3 text-center position-relative ${
                  formik.values?.isMultiStep ? "d-none" : ""
                }`}
              >
                <span
                  className="position-absolute p-2 px-4 fw-semibold top-50 translate-middle-y"
                  style={{ left: "calc(50% - 11rem)" }}
                >
                  {fillAutoSaveFormMutation.isLoading ||
                  editFilledAutoSaveFormMutation.isLoading ? (
                    <i>Saving...</i>
                  ) : fillAutoSaveFormMutation.isSuccess ||
                    editFilledAutoSaveFormMutation.isSuccess ? (
                    <span className="text-success">Saved ✅</span>
                  ) : null}
                </span>
                <Button
                  size="lg"
                  className="px-5"
                  type="button"
                  onClick={() => save()}
                  disabled={
                    !getFormQuery?.data?.form?.enabled ||
                    fillAutoSaveFormMutation.isLoading ||
                    editFilledAutoSaveFormMutation.isLoading
                  }
                >
                  Submit
                </Button>{" "}
              </div>
            </>
          ) : getFormQuery.isSuccess && getFormQuery?.data?.form ? (
            <div className="vh-100 vw-100 d-flex align-items-center justify-content-center text-center">
              <div>
                <Logo />
                <h1 className="display-5">Form Disabled</h1>
                <p className="">...</p>
              </div>
            </div>
          ) : null
        ) : null}

        <ModalLoader
          show={isLoadingIframe || isLoadingData || fillFormMutation.isLoading}
        />
      </div>
      {/*   {isPublic && (
        <CloudflareRobotCheckModal
          show={showRobotCheckModal}
          onHide={() => setShowRobotCheckModal(false)}
        />
      )} */}
    </>
  );
}
